import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Typography,
  Popconfirm,
} from 'antd';
import {
  CheckCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PendingItem } from '../../services/p15mjApi';
import { useP15MJApproval } from './hooks/useP15MJApproval';

const { Title, Text } = Typography;

// ============================================================
// Main Component
// ============================================================

const P15MJApproval: React.FC = () => {
  const {
    loading,
    approving,
    pendingItems,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchApprove,
  } = useP15MJApproval();

  // ============================================================
  // Table columns (5 columns: checkbox + eval_code + category + dept_remarks + rd_remarks)
  // ============================================================
  const columns: ColumnsType<PendingItem> = [
    {
      title: '評鑑代碼',
      dataIndex: 'eval_code',
      key: 'eval_code',
      width: 160,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '評鑑類別',
      dataIndex: 'category',
      key: 'category',
      width: 80,
      align: 'center',
    },
    {
      title: '部門核准備註',
      dataIndex: 'dept_remarks',
      key: 'dept_remarks',
      width: 200,
      ellipsis: true,
    },
    {
      title: '研發備註',
      dataIndex: 'rd_remarks',
      key: 'rd_remarks',
      ellipsis: true,
    },
  ];

  // ============================================================
  // Row selection
  // ============================================================
  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  // ============================================================
  // Filter form
  // ============================================================
  const handleSearch = () => { fetchPending(); };

  const handleFilterChange = (field: 'codeStart' | 'codeEnd', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 16 }}>廠商評鑑研發核准作業 (pw15mjf1)</Title>

      {/* Filter Bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label="評鑑代碼起始">
            <Input
              value={filter.codeStart}
              onChange={(e) => handleFilterChange('codeStart', e.target.value)}
              placeholder="例：EVAL2024001"
              style={{ width: 160 }}
              onPressEnter={handleSearch}
              maxLength={14}
            />
          </Form.Item>
          <Form.Item label="評鑑代碼結束">
            <Input
              value={filter.codeEnd}
              onChange={(e) => handleFilterChange('codeEnd', e.target.value)}
              placeholder="例：EVAL2024999"
              style={{ width: 160 }}
              onPressEnter={handleSearch}
              maxLength={14}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              查詢
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Summary & Actions */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆待研發核准
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認研發核准 ${selectedKeys.length} 筆廠商評鑑？`}
              onConfirm={batchApprove}
              okText="確認核准"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
            >
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={approving}
                disabled={selectedKeys.length === 0}
              >
                研發批量核准
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      {/* Data Table */}
      <Table<PendingItem>
        rowKey="eval_code"
        columns={columns}
        dataSource={pendingItems}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        scroll={{ x: 700 }}
        locale={{ emptyText: '無待研發核准資料' }}
      />
    </div>
  );
};

export default P15MJApproval;
